import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './ScritturaContabileDettaglioPage.api';
import './Scritture.css';

const MODULO = 'amministrazione.scritture_contabili';

type Stato = 'BOZZA' | 'CONFERMATA' | 'STORNATA';

interface ContoNode { id: number; codice: string; descrizione: string; movimentabile: boolean; attivo: boolean; figli: ContoNode[] }
interface ContoFlat { id: number; codice: string; descrizione: string }
interface RegistroCombo { id: number; codice: string; descrizione: string; attivo: boolean }
interface CausaleCombo { id: number; codice: string; descrizione: string; tipo: string; gestisceIva: boolean; sezionaleIvaDefaultId: number | null; registroContabileDefaultId: number | null; attivo: boolean }
interface SezionaleCombo { id: number; codice: string; descrizione: string; attivo: boolean }
interface CodiceIvaCombo { id: number; codice: string; descrizione: string; aliquota: number; attivo: boolean }
interface NomCombo { id: number; codice: string; ragioneSociale: string | null; nome: string | null; cognome: string | null }
interface ContoAnaliticoCombo { id: number; codice: string; descrizione: string; livello: number; attivo: boolean }

interface Riga {
  id?: number;
  ordine: number;
  contoCogeId: number | null;
  descrizione: string;
  importoDare: number;
  importoAvere: number;
  codiceIvaId: number | null;
  imponibileRiferimento: number | null;
  nominativoId: number | null;
  contoAnaliticoId: number | null;
}

interface Scrittura {
  id: number;
  societaId: number;
  registroContabileId: number;
  numero: number | null;
  anno: number | null;
  data: string;
  causaleId: number | null;
  sezionaleIvaId: number | null;
  numeroSezIva: number | null;
  annoSezIva: number | null;
  nominativoId: number | null;
  numeroDocumento: string | null;
  dataDocumento: string | null;
  descrizione: string;
  importoTotale: number;
  stato: Stato;
  scritturaStornataId: number | null;
  scritturaStornoId: number | null;
  note: string | null;
  righe: Array<{
    id: number; ordine: number; contoCogeId: number; descrizione: string;
    importoDare: number; importoAvere: number;
    codiceIvaId: number | null; imponibileRiferimento: number | null;
    nominativoId: number | null; contoAnaliticoId: number | null;
  }>;
}

function flattenLeaves(tree: ContoNode[]): ContoFlat[] {
  const out: ContoFlat[] = [];
  const rec = (nodes: ContoNode[]) => {
    for (const n of nodes) {
      if (n.movimentabile && n.attivo) out.push({ id: n.id, codice: n.codice, descrizione: n.descrizione });
      if (n.figli?.length) rec(n.figli);
    }
  };
  rec(tree);
  out.sort((a, b) => a.codice.localeCompare(b.codice, undefined, { numeric: true }));
  return out;
}

function nominativoLabel(n: { ragioneSociale?: string | null; nome?: string | null; cognome?: string | null; codice: string }): string {
  if (n.ragioneSociale) return n.ragioneSociale;
  const np = [n.nome, n.cognome].filter(Boolean).join(' ').trim();
  return np || n.codice;
}

function round2(x: number | string | null | undefined): number {
  if (x == null) return 0;
  const n = typeof x === 'number' ? x : Number(x);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

export default function ScritturaContabileDettaglioPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const isNew = idParam === 'nuova';
  const id = !isNew && idParam ? parseInt(idParam, 10) : null;
  const navigate = useNavigate();
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const { societaId: filtroSocietaId } = useFiltroSocieta();

  // Stato pagina
  const [societaIdPagina, setSocietaIdPagina] = useState<string>(filtroSocietaId || '');
  const [scrittura, setScrittura] = useState<Scrittura | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Combos
  const [conti, setConti] = useState<ContoFlat[]>([]);
  const [registri, setRegistri] = useState<RegistroCombo[]>([]);
  const [causali, setCausali] = useState<CausaleCombo[]>([]);
  const [sezionali, setSezionali] = useState<SezionaleCombo[]>([]);
  const [codiciIva, setCodiciIva] = useState<CodiceIvaCombo[]>([]);
  const [nominativi, setNominativi] = useState<NomCombo[]>([]);
  const [contiAnalitici, setContiAnalitici] = useState<ContoAnaliticoCombo[]>([]);

  // Form testata
  const [registroContabileId, setRegistroContabileId] = useState<string>('');
  const [data, setData] = useState<string>(new Date().toISOString().slice(0, 10));
  const [causaleId, setCausaleId] = useState<string>('');
  const [sezionaleIvaId, setSezionaleIvaId] = useState<string>('');
  const [nominativoId, setNominativoId] = useState<string>('');
  const [numeroDocumento, setNumeroDocumento] = useState<string>('');
  const [dataDocumento, setDataDocumento] = useState<string>('');
  const [descrizione, setDescrizione] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [righe, setRighe] = useState<Riga[]>([]);

  const [initialSnapshot, setInitialSnapshot] = useState<string>('');
  const formSnap = JSON.stringify({ registroContabileId, data, causaleId, sezionaleIvaId, nominativoId, numeroDocumento, dataDocumento, descrizione, note, righe });
  const isDirty = initialSnapshot !== '' && formSnap !== initialSnapshot;
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Hook contesto
  useBloccoRecordContesto({
    recordSocietaId: scrittura?.societaId,
    label: scrittura
      ? `Scrittura ${scrittura.numero != null ? `n.${scrittura.numero}/${scrittura.anno}` : 'in bozza'}`
      : (isNew ? 'Nuova scrittura' : 'Scrittura contabile'),
  });
  useDirtyForm(isDirty, scrittura ? `Scrittura ${scrittura.numero != null ? `n.${scrittura.numero}/${scrittura.anno}` : 'bozza'}` : 'Nuova scrittura');

  // ── Carica combo per società ───────────────────────────────────────────────
  const loadCombo = async (sid: number) => {
    // @api GET /api/coge/piano-conti?societaId= -> { success: true, data: [tree] }
    const tree = await apiFetch<{ success: boolean; data: ContoNode[] }>(`/api/coge/piano-conti?societaId=${sid}`);
    // @api GET /api/registri-contabili?societaId= -> { success: true, data: [...] }
    const resR = await apiFetch<{ success: boolean; data: RegistroCombo[] }>(`/api/registri-contabili?societaId=${sid}`);
    // @api GET /api/causali-contabili?societaId= -> { success: true, data: [...] }
    const resC = await apiFetch<{ success: boolean; data: CausaleCombo[] }>(`/api/causali-contabili?societaId=${sid}`);
    // @api GET /api/sezionali-iva?societaId= -> { success: true, data: [...] }
    const resS = await apiFetch<{ success: boolean; data: SezionaleCombo[] }>(`/api/sezionali-iva?societaId=${sid}`);
    // @api GET /api/codici-iva?societaId= -> [{ id, codice, descrizione, aliquota, attivo }]
    const resI = await apiFetch<CodiceIvaCombo[] | { data: CodiceIvaCombo[] }>(`/api/codici-iva?societaId=${sid}`);
    // @api GET /api/nominativi?societaId= -> [{ id, codice, ragioneSociale, nome, cognome }]
    const resN = await apiFetch<NomCombo[] | { data: NomCombo[] }>(`/api/nominativi?societaId=${sid}`);
    // @api GET /api/conti-analitici?societaId= -> { success: true, data: [tree con figli] }
    const resA = await apiFetch<{ success: boolean; data: any[] }>(`/api/conti-analitici?societaId=${sid}`);
    const ivaList = Array.isArray(resI) ? resI : ((resI as any).data || []);
    const nomList = Array.isArray(resN) ? resN : ((resN as any).data || []);
    // Flatten conti analitici (livello 3 = sottoconto movimentabile)
    const analiticiFlat: ContoAnaliticoCombo[] = [];
    const rec = (nodes: any[]) => {
      for (const n of nodes) {
        if (n.livello === 3 && n.attivo) analiticiFlat.push({ id: n.id, codice: n.codice, descrizione: n.descrizione, livello: n.livello, attivo: n.attivo });
        if (n.figli?.length) rec(n.figli);
      }
    };
    rec(resA.data || []);
    const registriAttivi = (resR.data || []).filter(r => r.attivo);
    setConti(flattenLeaves(tree.data || []));
    setRegistri(registriAttivi);
    setCausali((resC.data || []).filter(c => c.attivo));
    setSezionali((resS.data || []).filter(s => s.attivo));
    setCodiciIva(ivaList.filter((c: CodiceIvaCombo) => c.attivo));
    setNominativi(nomList);
    setContiAnalitici(analiticiFlat);
    return { registri: registriAttivi };
  };

  // ── Carica (o prepara nuova) ───────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      if (isNew) {
        const sid = filtroSocietaId ? parseInt(filtroSocietaId, 10) : null;
        if (!sid) { setError('Seleziona prima una società nella topbar'); setLoading(false); return; }
        setSocietaIdPagina(String(sid));
        const combo = await loadCombo(sid);
        // Registro: libro giornale unico, auto-selezionato (non serve sceglierlo)
        const regId = combo.registri.length === 1 ? String(combo.registri[0].id) : '';
        setRegistroContabileId(regId);
        const initForm = { registroContabileId: regId, data: new Date().toISOString().slice(0, 10), causaleId: '', sezionaleIvaId: '', nominativoId: '', numeroDocumento: '', dataDocumento: '', descrizione: '', note: '', righe: [] };
        setInitialSnapshot(JSON.stringify(initForm));
      } else if (id) {
        // @api GET /api/scritture-contabili/:id -> { success: true, data: { ..., righe: [...] }, logs: [...] }
        const res = await apiFetch<{ success: boolean; data: Scrittura }>(`/api/scritture-contabili/${id}`);
        const s = res.data;
        setScrittura(s);
        setSocietaIdPagina(String(s.societaId));
        await loadCombo(s.societaId);
        setRegistroContabileId(String(s.registroContabileId));
        setData(s.data ? new Date(s.data).toISOString().slice(0, 10) : '');
        setCausaleId(s.causaleId ? String(s.causaleId) : '');
        setSezionaleIvaId(s.sezionaleIvaId ? String(s.sezionaleIvaId) : '');
        setNominativoId(s.nominativoId ? String(s.nominativoId) : '');
        setNumeroDocumento(s.numeroDocumento || '');
        setDataDocumento(s.dataDocumento ? new Date(s.dataDocumento).toISOString().slice(0, 10) : '');
        setDescrizione(s.descrizione);
        setNote(s.note || '');
        const rgs: Riga[] = s.righe.map(r => ({
          id: r.id, ordine: r.ordine, contoCogeId: r.contoCogeId, descrizione: r.descrizione,
          importoDare: Number(r.importoDare), importoAvere: Number(r.importoAvere),
          codiceIvaId: r.codiceIvaId, imponibileRiferimento: r.imponibileRiferimento != null ? Number(r.imponibileRiferimento) : null,
          nominativoId: r.nominativoId, contoAnaliticoId: r.contoAnaliticoId,
        }));
        setRighe(rgs);
        setInitialSnapshot(JSON.stringify({
          registroContabileId: String(s.registroContabileId),
          data: s.data ? new Date(s.data).toISOString().slice(0, 10) : '',
          causaleId: s.causaleId ? String(s.causaleId) : '',
          sezionaleIvaId: s.sezionaleIvaId ? String(s.sezionaleIvaId) : '',
          nominativoId: s.nominativoId ? String(s.nominativoId) : '',
          numeroDocumento: s.numeroDocumento || '',
          dataDocumento: s.dataDocumento ? new Date(s.dataDocumento).toISOString().slice(0, 10) : '',
          descrizione: s.descrizione,
          note: s.note || '',
          righe: rgs,
        }));
      }
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id, isNew]);

  // ── Causale auto-popola defaults ───────────────────────────────────────────
  const onChangeCausale = (newCausaleId: string) => {
    setCausaleId(newCausaleId);
    const c = causali.find(x => String(x.id) === newCausaleId);
    if (c) {
      if (c.gestisceIva && c.sezionaleIvaDefaultId && !sezionaleIvaId) setSezionaleIvaId(String(c.sezionaleIvaDefaultId));
      if (c.registroContabileDefaultId && !registroContabileId) setRegistroContabileId(String(c.registroContabileDefaultId));
    }
  };

  // ── Applica causale: carica righe template ─────────────────────────────────
  const applicaCausale = async () => {
    if (!causaleId || !societaIdPagina) return;
    if (righe.length > 0 && !window.confirm('Le righe attuali verranno sostituite. Procedere?')) return;
    try {
      // @api POST /api/scritture-contabili/applica-causale { causaleId, societaId } -> { success: true, data: { causale, sezionaleIvaDefault, registroContabileDefault, righe: [...] } }
      const res = await apiFetch<{ success: boolean; data: { righe: Array<{ ordine: number; descrizione: string; contoCogeId: number | null }> } }>(
        '/api/scritture-contabili/applica-causale',
        { method: 'POST', body: JSON.stringify({ causaleId: Number(causaleId), societaId: Number(societaIdPagina) }) },
      );
      const nuove: Riga[] = res.data.righe.map(r => ({
        ordine: r.ordine,
        contoCogeId: r.contoCogeId,
        descrizione: r.descrizione,
        importoDare: 0,
        importoAvere: 0,
        codiceIvaId: null,
        imponibileRiferimento: null,
        nominativoId: null,
        contoAnaliticoId: null,
      }));
      setRighe(nuove);
    } catch (e: any) {
      window.alert(e.message || 'Errore applicazione causale');
    }
  };

  // ── Gestione righe ─────────────────────────────────────────────────────────
  const updateRiga = (idx: number, patch: Partial<Riga>) => {
    setRighe(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const aggiungiRiga = () => {
    setRighe(rs => [...rs, {
      ordine: rs.length + 1,
      contoCogeId: null,
      descrizione: '',
      importoDare: 0,
      importoAvere: 0,
      codiceIvaId: null,
      imponibileRiferimento: null,
      nominativoId: null,
      contoAnaliticoId: null,
    }]);
  };
  const rimuoviRiga = (idx: number) => {
    setRighe(rs => rs.filter((_, i) => i !== idx).map((r, i) => ({ ...r, ordine: i + 1 })));
  };

  // ── Totali / quadratura ────────────────────────────────────────────────────
  const totali = useMemo(() => {
    let dare = 0, avere = 0;
    for (const r of righe) {
      dare += round2(r.importoDare);
      avere += round2(r.importoAvere);
    }
    dare = round2(dare);
    avere = round2(avere);
    const diff = round2(dare - avere);
    return { dare, avere, diff, quadrata: Math.abs(diff) < 0.01 };
  }, [righe]);

  // ── Salva BOZZA / Modifica ─────────────────────────────────────────────────
  const salva = async () => {
    if (!societaIdPagina) { setFormError('societaId non valido'); return; }
    if (!registroContabileId) { setFormError('Seleziona un registro contabile'); return; }
    if (!data) { setFormError('Inserisci la data scrittura'); return; }
    if (!descrizione.trim()) { setFormError('Descrizione obbligatoria'); return; }

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        societaId: Number(societaIdPagina),
        registroContabileId: Number(registroContabileId),
        data,
        causaleId: causaleId ? Number(causaleId) : null,
        sezionaleIvaId: sezionaleIvaId ? Number(sezionaleIvaId) : null,
        nominativoId: nominativoId ? Number(nominativoId) : null,
        numeroDocumento: numeroDocumento.trim() || null,
        dataDocumento: dataDocumento || null,
        descrizione: descrizione.trim(),
        note: note.trim() || null,
        righe: righe.map(r => ({
          contoCogeId: r.contoCogeId,
          descrizione: r.descrizione,
          importoDare: r.importoDare,
          importoAvere: r.importoAvere,
          codiceIvaId: r.codiceIvaId,
          imponibileRiferimento: r.imponibileRiferimento,
          nominativoId: r.nominativoId,
          contoAnaliticoId: r.contoAnaliticoId,
        })),
      };
      if (isNew) {
        // @api POST /api/scritture-contabili { societaId, registroContabileId, data, causaleId, sezionaleIvaId, nominativoId, numeroDocumento, dataDocumento, descrizione, note, righe } -> { success: true, data: { id } }
        const res = await apiFetch<{ success: boolean; data: { id: number } }>('/api/scritture-contabili', {
          method: 'POST', body: JSON.stringify(payload),
        });
        navigate(`/amministrazione/scritture-contabili/${res.data.id}`, { replace: true });
      } else if (id) {
        // @api PUT /api/scritture-contabili/:id { ...stesso payload (registroContabileId immutabile in PUT)... } -> { success: true, data: { id } }
        await apiFetch(`/api/scritture-contabili/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
        setInitialSnapshot(formSnap);
        await load();
      }
    } catch (e: any) {
      setFormError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const conferma = async () => {
    if (!id) return;
    if (!totali.quadrata) { setFormError(`Scrittura non quadrata: Dare ${totali.dare} ≠ Avere ${totali.avere}`); return; }
    if (isDirty && !window.confirm('Ci sono modifiche non salvate. Salvarle prima della conferma?')) return;
    setSaving(true);
    setFormError('');
    try {
      if (isDirty) {
        await salva();
      }
      // @api PATCH /api/scritture-contabili/:id/conferma -> { success: true, data: { id, numero, anno, numeroSezIva, annoSezIva } }
      const res = await apiFetch<{ success: boolean; data: { numero: number; anno: number; numeroSezIva: number | null; annoSezIva: number | null } }>(
        `/api/scritture-contabili/${id}/conferma`, { method: 'PATCH' },
      );
      window.alert(`Scrittura confermata con numero ${res.data.numero}/${res.data.anno}${res.data.numeroSezIva ? ` (sezionale IVA: ${res.data.numeroSezIva}/${res.data.annoSezIva})` : ''}`);
      await load();
    } catch (e: any) {
      setFormError(e.message || 'Errore nella conferma');
    } finally {
      setSaving(false);
    }
  };

  const storna = async () => {
    if (!id) return;
    const dataDef = new Date().toISOString().slice(0, 10);
    const dataInput = window.prompt(`Data dello storno (default oggi ${dataDef}):`, dataDef);
    if (!dataInput) return;
    try {
      // @api PATCH /api/scritture-contabili/:id/storna { data } -> { success: true, data: { id, numero, anno } }
      const res = await apiFetch<{ success: boolean; data: { id: number; numero: number; anno: number } }>(
        `/api/scritture-contabili/${id}/storna`,
        { method: 'PATCH', body: JSON.stringify({ data: dataInput }) },
      );
      window.alert(`Storno generato come scrittura #${res.data.id} (n. ${res.data.numero}/${res.data.anno})`);
      navigate(`/amministrazione/scritture-contabili/${res.data.id}`);
    } catch (e: any) {
      window.alert(e.message || 'Errore nello storno');
    }
  };

  const eliminaBozza = async () => {
    if (!id || !scrittura || scrittura.stato !== 'BOZZA') return;
    if (!window.confirm('Eliminare definitivamente questa BOZZA?')) return;
    try {
      await apiFetch(`/api/scritture-contabili/${id}`, { method: 'DELETE' });
      navigate('/amministrazione/scritture-contabili');
    } catch (e: any) {
      window.alert(e.message || 'Errore');
    }
  };

  if (loading) return <Layout><div className="bop-empty-state">Caricamento…</div></Layout>;
  if (error) return <Layout><div className="bop-inline-error">{error}</div></Layout>;

  const readonly = !canEdit || (scrittura?.stato === 'STORNATA');
  const causaleScelta = causali.find(c => String(c.id) === causaleId);

  return (
    <Layout>
      <div className="scritture-contabili">
        <BopBackLink to="/amministrazione/scritture-contabili" label="Torna alle scritture contabili" />
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>
              {isNew ? 'Nuova scrittura contabile' : (
                <>
                  Scrittura {scrittura?.numero != null ? `n. ${scrittura.numero}/${scrittura.anno}` : '(in bozza)'}
                  {scrittura?.stato === 'STORNATA' && <span className="bop-badge bop-badge--warning sc-badge-inline">STORNATA</span>}
                  {scrittura?.stato === 'CONFERMATA' && <span className="bop-badge bop-badge--success sc-badge-inline">CONFERMATA</span>}
                  {scrittura?.stato === 'BOZZA' && <span className="bop-badge bop-badge--neutral sc-badge-inline">BOZZA</span>}
                </>
              )}
            </h2>
            {scrittura?.scritturaStornataId && <p>Questa è lo STORNO della scrittura #{scrittura.scritturaStornataId}</p>}
            {scrittura?.scritturaStornoId && <p>Stornata dalla scrittura #{scrittura.scritturaStornoId}</p>}
          </div>
          <div className="bop-flex">
            {!readonly && scrittura?.stato === 'BOZZA' && canDelete && (
              <button className="bop-btn bop-btn-danger" onClick={eliminaBozza}>Elimina</button>
            )}
            {scrittura?.stato === 'CONFERMATA' && canEdit && (
              <button className="bop-btn bop-btn-secondary" onClick={storna}>Storna</button>
            )}
            {!readonly && (
              <button className="bop-btn bop-btn-secondary" onClick={salva} disabled={saving || !isDirty}>
                {saving ? 'Salvo…' : (isNew ? 'Crea BOZZA' : 'Salva')}
              </button>
            )}
            {!readonly && !isNew && scrittura?.stato === 'BOZZA' && (
              <button className="bop-btn bop-btn-primary" onClick={conferma} disabled={saving || !totali.quadrata}>
                Conferma scrittura
              </button>
            )}
          </div>
        </div>

        {formError && <div className="bop-inline-error">{formError}</div>}

        {/* TESTATA */}
        <div className="bop-card sc-testata">
          <div className="sc-card-header"><h3>Testata</h3></div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Registro contabile</label>
              <select className="bop-select" value={registroContabileId} onChange={e => setRegistroContabileId(e.target.value)} disabled={readonly || !!scrittura?.numero || registri.length <= 1}>
                <option value="">Seleziona…</option>
                {registri.map(r => <option key={r.id} value={r.id}>{r.codice} — {r.descrizione}</option>)}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Data competenza *</label>
              <input type="date" className="bop-input" value={data} onChange={e => setData(e.target.value)} disabled={readonly} />
            </div>
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Causale</label>
              <select className="bop-select" value={causaleId} onChange={e => onChangeCausale(e.target.value)} disabled={readonly || !!scrittura?.numero}>
                <option value="">(scrittura libera, senza causale)</option>
                {causali.map(c => <option key={c.id} value={c.id}>{c.codice} — {c.descrizione}{c.gestisceIva ? ' [IVA]' : ''}</option>)}
              </select>
              {causaleId && !readonly && !scrittura?.numero && (
                <button type="button" className="bop-btn bop-btn-secondary sc-applica-btn" onClick={applicaCausale}>
                  ↻ Applica righe template
                </button>
              )}
            </div>
            {causaleScelta?.gestisceIva && (
              <div className="bop-form-group">
                <label className="bop-label">Sezionale IVA</label>
                <select className="bop-select" value={sezionaleIvaId} onChange={e => setSezionaleIvaId(e.target.value)} disabled={readonly || !!scrittura?.numero}>
                  <option value="">— non impostato —</option>
                  {sezionali.map(s => <option key={s.id} value={s.id}>{s.codice} — {s.descrizione}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Nominativo (cliente/fornitore)</label>
              <select className="bop-select" value={nominativoId} onChange={e => setNominativoId(e.target.value)} disabled={readonly}>
                <option value="">— nessuno —</option>
                {nominativi.map(n => <option key={n.id} value={n.id}>{n.codice} — {nominativoLabel(n)}</option>)}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">N° documento</label>
              <input className="bop-input" value={numeroDocumento} onChange={e => setNumeroDocumento(e.target.value)} disabled={readonly} placeholder="es. FT-2026-0123" />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Data documento</label>
              <input type="date" className="bop-input" value={dataDocumento} onChange={e => setDataDocumento(e.target.value)} disabled={readonly} />
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Descrizione scrittura *</label>
            <input className="bop-input" value={descrizione} onChange={e => setDescrizione(e.target.value)} disabled={readonly} placeholder="es. Fattura acquisto materiale ufficio" />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea className="bop-input" rows={2} value={note} onChange={e => setNote(e.target.value)} disabled={readonly} />
          </div>
        </div>

        {/* RIGHE */}
        <div className="bop-card sc-righe-card">
          <div className="sc-card-header">
            <h3>Righe Dare / Avere ({righe.length})</h3>
            {!readonly && <button className="bop-btn bop-btn-primary" onClick={aggiungiRiga}>+ Aggiungi riga</button>}
          </div>

          {righe.length === 0 ? (
            <div className="bop-empty-state">Nessuna riga. Aggiungi righe manualmente oppure applica una causale.</div>
          ) : (
            <div className="bop-table-wrapper">
              <table className="bop-table sc-righe-table">
                <thead>
                  <tr>
                    <th className="sc-col-ord">#</th>
                    <th className="sc-col-conto">Conto CO.GE *</th>
                    <th>Descrizione</th>
                    <th className="bop-text-right sc-col-imp">Dare</th>
                    <th className="bop-text-right sc-col-imp">Avere</th>
                    <th className="sc-col-iva">Cod. IVA</th>
                    <th className="bop-text-right sc-col-imp">Imponibile</th>
                    <th className="sc-col-nom">Nominativo</th>
                    <th className="sc-col-ana">Conto analitico</th>
                    <th className="sc-col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r, idx) => (
                    <tr key={idx}>
                      <td className="sc-col-ord">{r.ordine}</td>
                      <td>
                        <select className="bop-select" value={r.contoCogeId ? String(r.contoCogeId) : ''} onChange={e => updateRiga(idx, { contoCogeId: e.target.value ? Number(e.target.value) : null })} disabled={readonly}>
                          <option value="">Seleziona conto…</option>
                          {conti.map(c => <option key={c.id} value={c.id}>{c.codice} — {c.descrizione}</option>)}
                        </select>
                      </td>
                      <td>
                        <input className="bop-input" value={r.descrizione} onChange={e => updateRiga(idx, { descrizione: e.target.value })} disabled={readonly} />
                      </td>
                      <td>
                        <input type="number" step="0.01" min="0" className="bop-input bop-text-right" value={r.importoDare || ''} onChange={e => updateRiga(idx, { importoDare: e.target.value === '' ? 0 : Number(e.target.value), importoAvere: 0 })} disabled={readonly} />
                      </td>
                      <td>
                        <input type="number" step="0.01" min="0" className="bop-input bop-text-right" value={r.importoAvere || ''} onChange={e => updateRiga(idx, { importoAvere: e.target.value === '' ? 0 : Number(e.target.value), importoDare: 0 })} disabled={readonly} />
                      </td>
                      <td>
                        <select className="bop-select" value={r.codiceIvaId ? String(r.codiceIvaId) : ''} onChange={e => updateRiga(idx, { codiceIvaId: e.target.value ? Number(e.target.value) : null })} disabled={readonly}>
                          <option value="">—</option>
                          {codiciIva.map(c => <option key={c.id} value={c.id}>{c.codice} ({c.aliquota}%)</option>)}
                        </select>
                      </td>
                      <td>
                        <input type="number" step="0.01" min="0" className="bop-input bop-text-right" value={r.imponibileRiferimento != null ? r.imponibileRiferimento : ''} onChange={e => updateRiga(idx, { imponibileRiferimento: e.target.value === '' ? null : Number(e.target.value) })} disabled={readonly} />
                      </td>
                      <td>
                        <select className="bop-select" value={r.nominativoId ? String(r.nominativoId) : ''} onChange={e => updateRiga(idx, { nominativoId: e.target.value ? Number(e.target.value) : null })} disabled={readonly}>
                          <option value="">—</option>
                          {nominativi.map(n => <option key={n.id} value={n.id}>{n.codice} {nominativoLabel(n)}</option>)}
                        </select>
                      </td>
                      <td>
                        <select className="bop-select" value={r.contoAnaliticoId ? String(r.contoAnaliticoId) : ''} onChange={e => updateRiga(idx, { contoAnaliticoId: e.target.value ? Number(e.target.value) : null })} disabled={readonly}>
                          <option value="">—</option>
                          {contiAnalitici.map(c => <option key={c.id} value={c.id}>{c.codice} {c.descrizione}</option>)}
                        </select>
                      </td>
                      <td className="bop-text-right">
                        {!readonly && (
                          <BopRowActions actions={[
                            { icon: <RowIcon.Trash />, label: 'Elimina riga', onClick: () => rimuoviRiga(idx), variant: 'danger' },
                          ]} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="sc-totale-row">
                    <td colSpan={3} className="bop-text-right"><strong>Totali</strong></td>
                    <td className="bop-text-right"><strong>{formatCurrency(totali.dare)}</strong></td>
                    <td className="bop-text-right"><strong>{formatCurrency(totali.avere)}</strong></td>
                    <td colSpan={5}>
                      <span className={`sc-quadratura ${totali.quadrata ? 'sc-quadratura--ok' : 'sc-quadratura--ko'}`}>
                        {totali.quadrata
                          ? '✓ Quadrata'
                          : `✗ Differenza: ${formatCurrency(Math.abs(totali.diff))} ${totali.diff > 0 ? '(Dare > Avere)' : '(Avere > Dare)'}`}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
